/** Campi display per FIR (lista, dettaglio, trasmissioni). */

export type FirRecord = Record<string, unknown>;

const STATI_FORMULARIO: Record<string, string> = {
  InserimentoQuantita: "Inserimento quantità",
  InserimentoQuantitaTrasportoIniziale: "Inserimento quantità e trasporto iniziale",
  InserimentoTrasportoIniziale: "Inserimento trasporto iniziale",
  FirmaProduttoreTrasportatoreIniziale: "Firma produttore e trasportatore iniziale",
  FirmaTrasportatoreIniziale: "Firma trasportatore iniziale",
  FirmaProduttore: "Firma produttore",
  InserimentoTrasportoSuccessivo: "Inserimento trasporto successivo",
  FirmaTrasportatoreSuccessivo: "Firma trasportatore successivo",
  FirmaTrasbordoTotale: "Firma trasbordo totale",
  AcquisizioneFirmaTrasbordoParziale: "Acquisizione firma trasbordo parziale",
  FirmaTrasbordoParziale: "Firma trasbordo parziale",
  Consegnato: "Consegnato",
  Annullato: "Annullato",
};

const STATI_FISICI: Record<string, string> = {
  SP: "In polvere o pulverulento",
  S: "Solido",
  FP: "Fangoso",
  L: "Liquido",
  VS: "Vischioso sciropposo",
};

const PROVENIENZA: Record<string, string> = {
  U: "Urbano",
  S: "Speciale",
};

const TIPO_TRASPORTO: Record<string, string> = {
  Terrestre: "Terrestre",
  Marittimo: "Marittimo",
  Ferroviario: "Ferroviario",
};

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

function asArray(v: unknown): Record<string, unknown>[] {
  if (Array.isArray(v)) return v.map((x) => asRecord(x));
  return v ? [asRecord(v)] : [];
}

export function formatEerFir(eer: unknown, hp?: unknown): string {
  const raw = String(eer ?? "").replace(/\./g, "").trim();
  if (!raw) return "—";
  let out = raw;
  if (raw.length === 6) {
    out = `${raw.slice(0, 2)}.${raw.slice(2, 4)}.${raw.slice(4, 6)}`;
  }
  const hpList = Array.isArray(hp) ? hp : hp ? [hp] : [];
  const isPericoloso =
    hpList.length > 0 || String(eer).includes("*") || /^\d{6}$/.test(raw);
  if (isPericoloso && !out.includes("*")) out += " (*)";
  return out;
}

function formatDateIt(raw: unknown, withTime = false): string {
  if (!raw) return "—";
  const d = new Date(String(raw));
  if (isNaN(d.getTime())) return "—";
  if (withTime) {
    return d.toLocaleString("it-IT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleDateString("it-IT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function firNumero(f: FirRecord): string {
  return String(f.numero_fir ?? f.numeroFIR ?? "—");
}

export function firStatoLabel(f: FirRecord): string {
  const s = String(f.stato_formulario ?? f.stato ?? "");
  return (STATI_FORMULARIO[s] ?? s) || "—";
}

export function firStatoVariant(stato: string): "success" | "secondary" | "warning" | "outline" {
  const s = stato.toLowerCase();
  if (s.includes("consegnato") || s === "attivo") return "success";
  if (s.includes("annull")) return "secondary";
  if (s.includes("firma") || s.includes("inserimento")) return "warning";
  return "outline";
}

/** Stati che abilitano il pulsante «Firma» (XFIR). */
export function firNeedsSign(statoRaw: string): boolean {
  const s = statoRaw.toLowerCase();
  if (!s || s.includes("consegnato") || s.includes("annull")) return false;
  return s.includes("firma");
}

function soggettoNome(block: Record<string, unknown>): string {
  return String(block.denominazione ?? block.ragione_sociale ?? "—");
}

function destinatarioList(f: FirRecord): Record<string, unknown>[] {
  const dest = f.destinatari ?? f.destinatario;
  return asArray(dest);
}

function trasportatoreList(f: FirRecord): Record<string, unknown>[] {
  return asArray(f.trasportatori ?? f.trasportatore);
}

export function firListRowFields(f: FirRecord) {
  const dest = destinatarioList(f)[0];
  const trasp = trasportatoreList(f)[0];
  const hp = f.caratteristiche_pericolo;
  const qta = f.quantita;
  const um = f.unita_misura ?? f.unitaMisura;
  return {
    numero: firNumero(f),
    dataEmissione: formatDateIt(f.data_emissione ?? f.dataEmissione ?? f.data_creazione),
    stato: firStatoLabel(f),
    statoRaw: String(f.stato_formulario ?? f.stato ?? ""),
    produttore: soggettoNome(asRecord(f.produttore)),
    destinatario: dest ? soggettoNome(dest) : "—",
    trasportatore: trasp ? soggettoNome(trasp) : "—",
    eer: formatEerFir(f.codice_eer ?? f.eer, hp),
    quantita:
      qta != null && um
        ? `${Number(qta).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${String(um)}`
        : "—",
  };
}

export function firTrasmissioneRowFields(t: FirRecord) {
  const q = t.rifiuto_quantita;
  const um = t.rifiuto_unita_misura ?? "kg";
  return {
    identificativo: String(t.identificativo ?? "—"),
    numeroFir: String(t.numero_fir ?? "—"),
    dataTrasmissione: formatDateIt(t.data_trasmissione, true),
    dataEmissione: formatDateIt(t.data_emissione),
    eer: formatEerFir(t.codice_eer),
    quantita:
      q != null
        ? `${Number(q).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${String(um)}`
        : "—",
    utente: String(t.user_name ?? "—"),
    trasportatore: String(t.trasportatore ?? "—"),
    tipo: String(t.tipo ?? "—"),
    stato: String(t.stato ?? "—"),
  };
}

/** Dettaglio completo GET /formulari/{numero_fir} */
export function firDettaglioPartenza(d: FirRecord): Record<string, unknown> {
  return asRecord(d.dati_partenza);
}

export function firDettaglioNumero(d: FirRecord): string {
  const dp = firDettaglioPartenza(d);
  return String(dp.numero_fir ?? firNumero(d));
}

export function firDettaglioDataEmissione(d: FirRecord): string {
  const dp = firDettaglioPartenza(d);
  return formatDateIt(dp.data_emissione ?? d.data_creazione);
}

export type FirTabId = "rifiuto" | "soggetti" | "trasporto";

export function firTabHasData(d: FirRecord, tab: FirTabId): boolean {
  const dp = firDettaglioPartenza(d);
  switch (tab) {
    case "rifiuto":
      return Object.keys(asRecord(dp.rifiuto)).length > 0;
    case "soggetti":
      return (
        Object.keys(asRecord(dp.produttore)).length > 0 ||
        Object.keys(asRecord(dp.destinatario)).length > 0 ||
        (Array.isArray(dp.trasportatori) && dp.trasportatori.length > 0)
      );
    case "trasporto": {
      const dt = d.dati_trasporto;
      return Array.isArray(dt) ? dt.length > 0 : !!dt;
    }
    default:
      return false;
  }
}

function indirizzoCompleto(block: Record<string, unknown>): string {
  const ind = asRecord(block.indirizzo);
  if (ind.citta || ind.indirizzo) {
    const citta = asRecord(ind.citta);
    const parts = [
      ind.indirizzo,
      ind.civico,
      citta.nome_comune ?? citta.comune_id,
    ].filter(Boolean);
    return parts.join(", ") || "—";
  }
  const parts = [block.indirizzo, block.civico, block.comune_id].filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

export function firRifiutoCampi(d: FirRecord): { label: string; value: string }[] {
  const r = asRecord(firDettaglioPartenza(d).rifiuto);
  const qty = asRecord(r.quantita);
  const hp = r.caratteristiche_pericolo;
  const hpList = Array.isArray(hp) ? hp.join(", ") : hp ? String(hp) : "";
  const sf = r.stato_fisico
    ? `${r.stato_fisico} - ${STATI_FISICI[String(r.stato_fisico)] ?? r.stato_fisico}`
    : "—";
  const prov = r.provenienza
    ? `${r.provenienza} - ${PROVENIENZA[String(r.provenienza)] ?? r.provenienza}`
    : "—";
  const qVal =
    qty.valore != null
      ? `${qty.valore} ${qty.unita_misura ?? ""}`.trim()
      : "—";
  return [
    { label: "Codice EER", value: formatEerFir(r.codice_eer, hp) },
    { label: "Quantità", value: qVal },
    { label: "Stato fisico", value: sf },
    { label: "Caratteristiche pericolo", value: hpList || "—" },
    { label: "Provenienza", value: prov },
    { label: "Descrizione del rifiuto", value: String(r.descrizione ?? "—") },
    {
      label: "Normativa ADR",
      value: r.trasporto_adr === true ? "Sì" : r.trasporto_adr === false ? "No" : "—",
    },
  ];
}

function soggettoCampi(
  titolo: string,
  block: Record<string, unknown>,
  extra?: { label: string; value: string }[],
): { label: string; value: string }[] {
  if (!Object.keys(block).length) return [];
  const out: { label: string; value: string }[] = [
    { label: titolo, value: soggettoNome(block) },
    { label: `${titolo} — Codice fiscale`, value: String(block.codice_fiscale ?? "—") },
  ];
  if (block.num_iscr_sito) {
    out.push({ label: `${titolo} — Unità locale`, value: String(block.num_iscr_sito) });
  }
  const addr = indirizzoCompleto(block);
  if (addr !== "—") out.push({ label: `${titolo} — Indirizzo`, value: addr });
  const auth = asRecord(block.autorizzazione);
  if (auth.numero) {
    const att = block.attivita ? ` (${block.attivita})` : "";
    out.push({
      label: `${titolo} — Autorizzazione`,
      value: `${auth.tipo ?? ""} ${auth.numero}${att}`.trim(),
    });
  }
  if (block.tipo_trasporto) {
    out.push({
      label: `${titolo} — Tipo trasporto`,
      value: TIPO_TRASPORTO[String(block.tipo_trasporto)] ?? String(block.tipo_trasporto),
    });
  }
  if (extra) out.push(...extra);
  return out;
}

export function firSoggettiCampi(d: FirRecord): { label: string; value: string }[] {
  const dp = firDettaglioPartenza(d);
  const out: { label: string; value: string }[] = [];
  out.push(...soggettoCampi("Produttore", asRecord(dp.produttore)));
  out.push(...soggettoCampi("Destinatario", asRecord(dp.destinatario)));
  asArray(dp.trasportatori).forEach((t, i) => {
    out.push(
      ...soggettoCampi(
        asArray(dp.trasportatori).length > 1 ? `Trasportatore ${i + 1}` : "Trasportatore",
        t,
      ),
    );
  });
  return out.length ? out : [{ label: "—", value: "Nessun soggetto indicato" }];
}

export function firTrasportoCampi(d: FirRecord): { label: string; value: string }[] {
  const dt = d.dati_trasporto;
  const list = Array.isArray(dt) ? dt : dt ? [dt] : [];
  if (!list.length) return [{ label: "—", value: "Dati trasporto non ancora inseriti" }];
  const out: { label: string; value: string }[] = [];
  list.forEach((raw, i) => {
    const t = asRecord(raw);
    const prefix = list.length > 1 ? `Trasporto ${i + 1}` : "Trasporto";
    const cond = asRecord(t.conducente);
    const nomeCond = [cond.cognome, cond.nome].filter(Boolean).join(" ");
    if (t.tipo_trasporto) {
      out.push({
        label: `${prefix} — Modalità`,
        value: TIPO_TRASPORTO[String(t.tipo_trasporto)] ?? String(t.tipo_trasporto),
      });
    }
    out.push({
      label: `${prefix} — Data inizio trasporto`,
      value: formatDateIt(t.data_ora_inizio_trasporto, true),
    });
    if (nomeCond) out.push({ label: `${prefix} — Conducente`, value: nomeCond });
    if (t.targa_automezzo) {
      out.push({ label: `${prefix} — Targa automezzo`, value: String(t.targa_automezzo) });
    }
    if (t.targa_rimorchio) {
      out.push({ label: `${prefix} — Targa rimorchio`, value: String(t.targa_rimorchio) });
    }
  });
  return out;
}
